import {
  addToDate,
  isDateInFuture,
  useHttpClient,
  useLocalStorage,
  useNetworkStatus,
} from '@airship-platform/airship-patterns';
import {
  buildDurationBillingIndex,
  type DurationBillingIndex,
} from '@airship-platform/proposal-financing-feature/utils';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useOfflineProviderInit } from '../../rxdb/useOfflineProviderInit';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import RxDBSetupNotificationAlert from '../../rxdb/utils/RxDBSetupNotificationAlert';
import { InvoiceTemplate, MembershipType } from './membershipData.collection';

const OFFLINE_KEY = 'offline-sync-membership';
const OFFLINE_SYNC_HOURS = 24;
const syncedInLastNHours = (offlineSync: string, hours = OFFLINE_SYNC_HOURS) =>
  isDateInFuture(addToDate(hours, 'h', offlineSync));

interface MembershipDataContextProps {
  syncMembershipData: () => Promise<void>;
  membershipDataSync: { error?: string | null; lastSyncTime?: string };
  durationBillingIndex: DurationBillingIndex;
}

const MembershipDataContext = createContext<
  MembershipDataContextProps | undefined
>(undefined);

const OfflineMembershipDataOverride: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const data = useOfflineDbData();
  const httpClient = useHttpClient();
  const isOnline = useNetworkStatus();
  const [lastSyncTime, setLastSyncTime] = useLocalStorage(OFFLINE_KEY);
  const [error, setError] = useState<string | null>(null);
  const [membershipData, setMembershipData] = useState<{
    membershipTypes: MembershipType[];
    invoiceTemplates: InvoiceTemplate[];
  }>({ membershipTypes: [], invoiceTemplates: [] });

  const fetchAndCache = useCallback(() => {
    const setSyncedNow = () =>
      Promise.resolve(setLastSyncTime(addToDate(0, 'h').toISOString()));

    return httpClient
      .get<{
        membershipTypes: MembershipType[];
        invoiceTemplates: InvoiceTemplate[];
      }>('/appointments/sync/membership')
      .then(({ membershipTypes, invoiceTemplates }) =>
        Promise.all([
          data.bulkUpsert('membership_types', membershipTypes),
          data.bulkUpsert('invoice_templates', invoiceTemplates),
        ]).then(setSyncedNow)
      );
  }, [data, httpClient, setLastSyncTime]);

  const populateMembershipDataState = useCallback(
    () =>
      Promise.all([
        data.find('membership_types'),
        data.find('invoice_templates'),
      ])
        .then(([membershipTypes, invoiceTemplates]) =>
          setMembershipData({ membershipTypes, invoiceTemplates })
        )
        .catch((error) => setError(`${error}`)),
    [data]
  );

  const syncMembershipData = useCallback(async () => {
    setError(null);
    if (isOnline()) {
      await fetchAndCache().catch((error) => setError(`${error}`));
    }
    await populateMembershipDataState();
  }, [isOnline, fetchAndCache, populateMembershipDataState]);

  const init = useCallback(async () => {
    await populateMembershipDataState();

    if (!lastSyncTime || !syncedInLastNHours(lastSyncTime)) {
      syncMembershipData();
    }
  }, [lastSyncTime, syncMembershipData, populateMembershipDataState]);

  const durationBillingIndex = useMemo(
    () =>
      buildDurationBillingIndex(
        membershipData.membershipTypes,
        membershipData.invoiceTemplates
      ),
    [membershipData]
  );

  const { initError } = useOfflineProviderInit(init);

  if (initError) {
    return (
      <RxDBSetupNotificationAlert
        error={initError}
        entityName="membership data"
      />
    );
  }

  return (
    <MembershipDataContext.Provider
      value={{
        membershipDataSync: { error, lastSyncTime },
        syncMembershipData,
        durationBillingIndex,
      }}
    >
      {children}
    </MembershipDataContext.Provider>
  );
};

export const OfflineMembershipDataProvider: React.FC<PropsWithChildren> = ({
  children,
}) => <OfflineMembershipDataOverride>{children}</OfflineMembershipDataOverride>;

export const useMembershipData = () => {
  const context = useContext(MembershipDataContext);
  if (context === undefined) {
    throw new Error(
      'useMembershipData must be used within an OfflineMembershipDataProvider'
    );
  }
  return context;
};
